import { useStoreLogger } from '../logger.ts'
import { CountryGetList, StateGetList, CodeGetList, CultureGetList } from '../api/CountryCulture.ts'
import type {
  ICountryResponse,
  IStateResponse,
  ICodeResponse,
  ICulture, ICultureResponse
} from '../types/CountryCultureTypes'
import { GetCacheItem, SetCacheItem } from '../services/CacheService.ts'

export const CountriesLocalStorageKey = 'Countries'

export function GetCountriesFromLocalStorage(): ICountryResponse | null {
  const logger = useStoreLogger()
  logger.debug('GetCountriesFromLocalStorage')
  const key = CountriesLocalStorageKey
  const cacheItem = GetCacheItem<string>(key)
  if (cacheItem) {
    logger.debug('Country List found in local storage')
    const countries = JSON.parse(cacheItem) as ICountryResponse
    return countries
  }
  logger.debug('Country List not found in local storage')
  return null
}

export async function GetCountriesFromLocalStorageOrApi(): Promise<ICountryResponse | null> {
  const logger = useStoreLogger()
  logger.debug('GetCountriesFromLocalStorageOrApi')

  let countries = GetCountriesFromLocalStorage()
  if (countries) return countries

  const countryResponse = await CountryGetList()
  if (countryResponse) {
    SaveCountriesToLocalStorage(countryResponse)
    countries = GetCountriesFromLocalStorage()
  }
  return countryResponse
}

export function SaveCountriesToLocalStorage(countryResponse: ICountryResponse) {
  const newValue = JSON.stringify(countryResponse)
  const logger = useStoreLogger()
  logger.debug('SaveCountriesToLocalStorage', newValue)
  const key = CountriesLocalStorageKey
  SetCacheItem(key, newValue, 60 * 60)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

export const StatesLocalStorageKey = 'States_'

export function GetStatesFromLocalStorage(countryCode: string): IStateResponse | null {
  const logger = useStoreLogger()
  logger.debug('GetStatesFromLocalStorage')
  const key = StatesLocalStorageKey + countryCode
  const cacheItem = GetCacheItem<string>(key)
  if (cacheItem) {
    logger.debug('State List found in local storage')
    const states = JSON.parse(cacheItem) as IStateResponse
    return states
  }
  logger.debug('Country List not found in local storage')
  return null
}

export async function GetStatesFromLocalStorageOrApi(countryCode: string): Promise<IStateResponse | null> {
  const logger = useStoreLogger()
  logger.debug('GetStatesFromLocalStorageOrApi')

  let states = GetStatesFromLocalStorage(countryCode)
  if (states) return states

  const stateResponse = await StateGetList(countryCode)
  if (stateResponse) {
    SaveStatesToLocalStorage(stateResponse, countryCode)
    states = GetStatesFromLocalStorage(countryCode)
  }
  return stateResponse
}

export function SaveStatesToLocalStorage(stateResponse: IStateResponse, countryCode: string) {
  const newValue = JSON.stringify(stateResponse)
  const logger = useStoreLogger()
  logger.debug('SaveStatesToLocalStorage', newValue)
  const key = StatesLocalStorageKey + countryCode
  SetCacheItem(key, newValue, 60 * 60)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

const CodeLocalStorageKey = 'Codes_'

export async function GetCodesFromLocalStorageOrApi(categoryId: string): Promise<ICodeResponse | null> {
  const logger = useStoreLogger()
  logger.debug('GetCodesFromLocalStorageOrApi')

  let codes = GetCodesFromLocalStorage(categoryId)
  if (codes) return codes

  const codeResponse = await CodeGetList(categoryId)
  if (codeResponse) {
    SaveCodesToLocalStorage(codeResponse, categoryId)
    codes = GetCodesFromLocalStorage(categoryId)
  }
  return codeResponse
}

export function GetCodesFromLocalStorage(categoryId: string): ICodeResponse | null {
  const logger = useStoreLogger()
  logger.debug('GetCodesFromLocalStorage')
  const key = CodeLocalStorageKey + categoryId
  const cacheItem = GetCacheItem<string>(key)
  if (cacheItem) {
    logger.debug('Code List found in local storage')
    const codes = JSON.parse(cacheItem) as ICodeResponse
    return codes
  }
  logger.debug('Code List not found in local storage')
  return null
}

export function SaveCodesToLocalStorage(codeResponse: ICodeResponse, categoryId: string) {
  const newValue = JSON.stringify(codeResponse)
  const logger = useStoreLogger()
  logger.debug('SaveCodesToLocalStorage', newValue)
  const key = CodeLocalStorageKey + categoryId
  SetCacheItem(key, newValue, 60 * 60)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}


export const NLSCultureLocalStorageKey = 'Cultures'

export async function GetCulturesFromLocalStorageOrApi(
): Promise<ICulture[]> {
  const logger = useStoreLogger()
  logger.debug('GetCulturesFromLocalStorageOrApi')

  let gotc = GetCultureFromLocalStorage()
  if (gotc) return gotc

  const gotcResponse = await CultureGetList()
  logger.debug('GetCulturesFromLocalStorageOrApi', gotcResponse)
  if (gotcResponse != null)
  {
    SaveCultureToLocalStorage(gotcResponse)
    gotc = GetCultureFromLocalStorage()
  }

  if (gotc != null)
  {
    return gotc
  }
  else
  {
    const emptyArray : ICulture[] = []
    return emptyArray
  }
}

export function GetCultureFromLocalStorage(
): ICulture[] | null {
  const logger = useStoreLogger()
  logger.debug('GetCultureFromLocalStorage')

  const json = localStorage.getItem(NLSCultureLocalStorageKey)
  if (json) {
    logger.debug('Culture found in local storage')
    const gotc: ICulture[] = JSON.parse(json)
    if (gotc) {
      return gotc
    }
  }
  logger.debug('Culture not found in local storage')
  return null
}

export function SaveCultureToLocalStorage(gotcResponse: ICultureResponse) {
  const newValue = JSON.stringify(gotcResponse.cultures)
  const logger = useStoreLogger()
  logger.debug('SaveCultureToLocalStorage', newValue)
  const key = NLSCultureLocalStorageKey
  localStorage.setItem(key, newValue)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

